import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Minus, Store, Users, Star, Wallet,
  ShoppingBag, Heart, Share2, MessageCircle, Eye, Camera, Clock,
  AlertCircle, RefreshCw, ChevronDown, Check, BarChart3, Table as TableIcon, XCircle,
} from 'lucide-react';
import { host } from '../script/variables';
import '../style/analytics.css';

/* ═══════════════════════════════════════════════════════════════
   PALETTE

   Series colours are a validated categorical set (adjacent-pair
   CVD ΔE 9.1, normal-vision ΔE 19.6 against the white card
   surface). Slots are assigned BY ENTITY and never by rank, so
   filtering the outlet list never repaints the survivors.

   Chrome/ink/status come from the brand tokens in theme.css —
   the brand's own Deep Harbor is too dark and too desaturated to
   work as a data series, so it stays on chrome where it belongs.
═══════════════════════════════════════════════════════════════ */
const SERIES = [
  '#2a78d6', '#eb6834', '#1baf7a', '#eda100',
  '#e87ba4', '#008300', '#4a3aa7', '#e34948',
];

const INK = {
  primary:   '#0F2534',
  secondary: '#5E7A8C',
  muted:     '#8FA3AF',
  grid:      '#E6E2D4',
  axis:      '#CFD8DD',
  surface:   '#FFFFFF',
  context:   '#C3CDD4', // de-emphasis grey for "previous period"
};

const STATUS = { good: '#2E7D52', bad: '#B5432F', flat: '#5E7A8C' };

// Sequential blue, light → dark. Index 0 means "near zero" and is
// allowed to recede toward the card surface.
const HEAT = [
  '#EFF5FD', '#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#104281',
];

const MAX_SERIES = SERIES.length;

/* ═══════════════════════════════════════════════════════════════
   FORMATTERS
═══════════════════════════════════════════════════════════════ */
const n0 = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

// Indian money shorthand — crore / lakh / thousand.
const money = (v) => {
  const x = Number(v || 0);
  const s = x < 0 ? '-' : '';
  const a = Math.abs(x);
  if (a >= 1e7) return `${s}₹${(a / 1e7).toFixed(a >= 1e8 ? 0 : 1)}Cr`;
  if (a >= 1e5) return `${s}₹${(a / 1e5).toFixed(a >= 1e6 ? 0 : 1)}L`;
  if (a >= 1e3) return `${s}₹${(a / 1e3).toFixed(a >= 1e4 ? 0 : 1)}k`;
  return `${s}₹${Math.round(a)}`;
};

const moneyFull = (v) => `₹${n0(Math.round(Number(v || 0)))}`;

const compact = (v) => {
  const a = Math.abs(Number(v || 0));
  if (a >= 1e7) return `${(a / 1e7).toFixed(1)}Cr`;
  if (a >= 1e5) return `${(a / 1e5).toFixed(1)}L`;
  if (a >= 1e3) return `${(a / 1e3).toFixed(1)}k`;
  return n0(a);
};

// Lakh/crore shorthand keeping up to two decimals, for headline
// figures — "₹1.24Cr" rather than the axis-style "₹1.2Cr".
const shortNum = (v) => {
  const x = Number(v || 0);
  const a = Math.abs(x);
  const s = x < 0 ? '-' : '';
  const fmt = (n) => n.toFixed(2).replace(/\.?0+$/, '');
  if (a >= 1e7) return `${s}${fmt(a / 1e7)}Cr`;
  if (a >= 1e5) return `${s}${fmt(a / 1e5)}L`;
  return `${s}${n0(a)}`;
};

/* Headline figure: exact while it fits, lakh/crore shorthand once it
   would crowd its slot. The exact value is kept on hover. */
const Figure = ({ value, money: isMoney = false, max = 10 }) => {
  const full = isMoney ? moneyFull(value) : n0(value);
  if (full.length <= max) return full;
  const x = Number(value || 0);
  const short = isMoney ? `${x < 0 ? '-' : ''}₹${shortNum(Math.abs(x))}` : shortNum(x);
  return <abbr className="an-abbr" title={full}>{short}</abbr>;
};

const pct1 = (v) => `${Number(v || 0).toFixed(1)}%`;

const growth = (curr, prev) => {
  if (prev == null || prev === 0) return null;
  return ((Number(curr || 0) - prev) / Math.abs(prev)) * 100;
};

const dayLabel = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d} ${months[Number(m) - 1]}`;
};

const hourLabel = (h) => {
  if (h === 0) return '12a';
  if (h === 12) return '12p';
  return h < 12 ? `${h}a` : `${h - 12}p`;
};

const timeAgo = (d) => {
  if (!d) return '';
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// Sentence-style: "dine-in" → "Dine-in", not "Dine-In".
const titleCase = (s) => String(s || '').replace(/(^|\s)\w/g, (c) => c.toUpperCase());

/* ═══════════════════════════════════════════════════════════════
   DATE RANGES

   Each preset also yields the immediately preceding window of the
   same length, so every headline number can carry a like-for-like
   delta without the owner choosing a comparison.
═══════════════════════════════════════════════════════════════ */
const iso = (d) => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const shift = (d, days) => { const r = new Date(d); r.setDate(r.getDate() + days); return r; };
const daysBetween = (a, b) =>
  Math.round((new Date(`${b}T00:00:00+05:30`) - new Date(`${a}T00:00:00+05:30`)) / 86400000) + 1;

const buildRange = (key, customFrom, customTo) => {
  const today = new Date();

  const rolling = (len, label) => {
    const to   = iso(today);
    const from = iso(shift(today, -(len - 1)));
    const prevTo   = iso(shift(today, -len));
    const prevFrom = iso(shift(today, -(len * 2 - 1)));
    return { key, label, from, to, prevFrom, prevTo };
  };

  switch (key) {
    case 'today': {
      const to = iso(today);
      const prev = iso(shift(today, -1));
      return { key, label: 'Today', from: to, to, prevFrom: prev, prevTo: prev };
    }
    case '7d':  return rolling(7,  'Last 7 days');
    case '30d': return rolling(30, 'Last 30 days');
    case '90d': return rolling(90, 'Last 90 days');
    case 'mtd': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const prevStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const prevEnd = shift(start, -1);
      return {
        key, label: 'This month',
        from: iso(start), to: iso(today),
        prevFrom: iso(prevStart), prevTo: iso(prevEnd),
      };
    }
    case 'ytd': {
      const start = new Date(today.getFullYear(), 0, 1);
      return {
        key, label: 'This year',
        from: iso(start), to: iso(today),
        prevFrom: `${today.getFullYear() - 1}-01-01`,
        prevTo:   iso(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())),
      };
    }
    case 'custom': {
      if (!customFrom || !customTo) return rolling(30, 'Last 30 days');
      const len = Math.max(1, daysBetween(customFrom, customTo));
      const prevTo   = iso(shift(new Date(`${customFrom}T00:00:00+05:30`), -1));
      const prevFrom = iso(shift(new Date(`${customFrom}T00:00:00+05:30`), -len));
      return { key, label: 'Custom range', from: customFrom, to: customTo, prevFrom, prevTo };
    }
    default: return rolling(30, 'Last 30 days');
  }
};

const RANGE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: '7d',    label: 'Last 7 days' },
  { key: '30d',   label: 'Last 30 days' },
  { key: '90d',   label: 'Last 90 days' },
  { key: 'mtd',   label: 'This month' },
  { key: 'ytd',   label: 'This year' },
];

/* ═══════════════════════════════════════════════════════════════
   API
═══════════════════════════════════════════════════════════════ */
const fetchPortfolio = async (range, scope, signal) => {
  const token = localStorage.getItem('partnertoken');
  const res = await fetch(`${host}/partner/portfolio_analytics`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    signal,
    body: JSON.stringify({
      fromDate:     range.from,
      toDate:       range.to,
      prevFromDate: range.prevFrom,
      prevToDate:   range.prevTo,
      restaurantIds: scope === 'all' ? [] : [scope],
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Could not load analytics');
  }
  return json;
};

/* ═══════════════════════════════════════════════════════════════
   PRIMITIVES
═══════════════════════════════════════════════════════════════ */

/* Delta — direction is always carried by an icon + the words
   "vs previous", never by colour alone. `goodWhen` flips the
   colour for metrics where a rise is bad (cancellations, dues). */
// A near-zero base makes percentages absurd ("+14,250%"), so past
// 10× the change reads as a multiple instead.
const pctChange = (g) => {
  if (g >= 1000) return `${Math.round(g / 100 + 1)}×`;
  if (Math.abs(g) <= 0.05) return '0%';
  return `${g > 0 ? '+' : ''}${g.toFixed(Math.abs(g) >= 100 ? 0 : 1)}%`;
};

const Delta = ({ curr, prev, goodWhen = 'up' }) => {
  const g = growth(curr, prev);
  if (g === null) {
    // prev === 0 is a real measurement, not missing data.
    if (prev === 0 && Number(curr) > 0) {
      return (
        <span className={`an-delta an-delta--${goodWhen === 'up' ? 'good' : 'bad'}`}>
          <TrendingUp size={13} aria-hidden="true" />New
          <span className="an-delta__ctx">none previously</span>
        </span>
      );
    }
    return (
      <span className="an-delta an-delta--flat">
        <Minus size={13} aria-hidden="true" />{prev === 0 ? 'No change' : 'No prior data'}
      </span>
    );
  }
  const rising = g > 0.05;
  const falling = g < -0.05;
  const good = goodWhen === 'up' ? rising : falling;
  const bad  = goodWhen === 'up' ? falling : rising;
  const tone = good ? 'good' : bad ? 'bad' : 'flat';
  const Icon = rising ? TrendingUp : falling ? TrendingDown : Minus;

  return (
    <span className={`an-delta an-delta--${tone}`}>
      <Icon size={13} aria-hidden="true" />
      {pctChange(g)}
      <span className="an-delta__ctx">vs previous</span>
    </span>
  );
};

/* Stat tile — label · value · delta. The value uses proportional
   figures (tabular-nums is reserved for table columns). */
const StatTile = ({ label, value, curr, prev, goodWhen = 'up', icon: Icon, hint, tone }) => (
  <div className={`an-tile ${tone ? `an-tile--${tone}` : ''}`}>
    <div className="an-tile__head">
      {Icon && <Icon size={15} aria-hidden="true" />}
      <span className="an-tile__label">{label}</span>
    </div>
    <div className="an-tile__value">{value}</div>
    {prev !== undefined
      ? <Delta curr={curr} prev={prev} goodWhen={goodWhen} />
      : hint && <span className="an-tile__hint">{hint}</span>}
  </div>
);

/* Chart card — every chart ships with a table-view twin so no
   value is ever reachable only by hovering a coloured mark. */
const ChartCard = ({ title, subtitle, children, table, actions, className = '', wide }) => {
  const [view, setView] = useState('chart');
  const hasTable = !!(table && table.rows && table.rows.length);

  return (
    <section className={`an-card ${wide ? 'an-card--wide' : ''} ${className}`}>
      <header className="an-card__head">
        <div>
          <h3 className="an-card__title">{title}</h3>
          {subtitle && <p className="an-card__sub">{subtitle}</p>}
        </div>
        <div className="an-card__actions">
          {actions}
          {hasTable && (
            <div className="an-toggle" role="group" aria-label={`${title} view`}>
              <button
                type="button"
                className={view === 'chart' ? 'is-active' : ''}
                onClick={() => setView('chart')}
                aria-pressed={view === 'chart'}
                title="Chart view"
              >
                <BarChart3 size={14} aria-hidden="true" /><span>Chart</span>
              </button>
              <button
                type="button"
                className={view === 'table' ? 'is-active' : ''}
                onClick={() => setView('table')}
                aria-pressed={view === 'table'}
                title="Table view"
              >
                <TableIcon size={14} aria-hidden="true" /><span>Table</span>
              </button>
            </div>
          )}
        </div>
      </header>

      {view === 'chart' || !hasTable ? (
        <div className="an-card__body">{children}</div>
      ) : (
        <div className="an-card__body an-tablewrap">
          <table className="an-table">
            <thead>
              <tr>
                {table.columns.map((c) => (
                  <th key={c.key} className={c.align === 'right' ? 'ta-r' : ''} scope="col">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, i) => (
                <tr key={row.key ?? i}>
                  {table.columns.map((c) => (
                    <td key={c.key} className={c.align === 'right' ? 'ta-r' : ''}>
                      {c.format ? c.format(row[c.key], row) : row[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

/* Shared recharts tooltip shell. */
const TipBox = ({ title, rows }) => (
  <div className="an-tip">
    {title && <p className="an-tip__title">{title}</p>}
    {rows.map((r, i) => (
      <p key={i} className="an-tip__row">
        <span className="an-tip__key">
          {r.color && <i className="an-tip__dot" style={{ background: r.color }} aria-hidden="true" />}
          {r.label}
        </span>
        <span className="an-tip__val">{r.value}</span>
      </p>
    ))}
  </div>
);

const EmptyState = ({ icon: Icon = BarChart3, title, body }) => (
  <div className="an-empty">
    <Icon size={30} strokeWidth={1.4} aria-hidden="true" />
    <p className="an-empty__title">{title}</p>
    {body && <p className="an-empty__body">{body}</p>}
  </div>
);

/* Part-to-whole bar — built from divs so segment gaps are exactly
   2px of card surface and labels can be measured before they are
   placed inside a segment. */
const ShareBar = ({ segments, total, formatValue = n0 }) => {
  if (!total) return <EmptyState title="Nothing in this period yet" />;
  return (
    <div className="an-share">
      <div className="an-share__track">
        {segments.map((s) => {
          const share = (s.value / total) * 100;
          return (
            <div
              key={s.label}
              className="an-share__seg"
              style={{ width: `${share}%`, background: s.color }}
              title={`${s.label}: ${formatValue(s.value)} (${share.toFixed(1)}%)`}
            >
              {/* Only label inside the segment when it comfortably fits. */}
              {share >= 12 && <span className="an-share__inlabel">{share.toFixed(0)}%</span>}
            </div>
          );
        })}
      </div>
      <ul className="an-legend an-legend--stack">
        {segments.map((s) => (
          <li key={s.label}>
            <i className="an-legend__dot" style={{ background: s.color }} aria-hidden="true" />
            <span className="an-legend__label">{s.label}</span>
            <span className="an-legend__val">
              {formatValue(s.value)}
              <em>{((s.value / total) * 100).toFixed(1)}%</em>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* Ranked horizontal bars — one series, therefore one colour for
   every bar (a value-ramp here would double-encode bar length). */
/* `columns` splits a long list into two column-major halves once the
   card is wide enough, so a full-width card isn't 8 thin rows. */
const RankedBars = ({ rows, valueKey = 'value', labelKey = 'label', format = n0, color = SERIES[0], emptyText, columns }) => {
  const max = Math.max(...rows.map((r) => Math.abs(r[valueKey])), 1);
  if (!rows.length) return <EmptyState title={emptyText || 'No data in this period'} />;
  return (
    <ul
      className={`an-ranked ${columns && rows.length > 3 ? 'an-ranked--cols' : ''}`}
      style={{ '--an-rows': Math.ceil(rows.length / 2) }}
    >
      {rows.map((r, i) => (
        <li key={r.key ?? r[labelKey] ?? i}>
          <span className="an-ranked__rank">{i + 1}</span>
          <span className="an-ranked__label" title={r[labelKey]}>{r[labelKey]}</span>
          <span className="an-ranked__track">
            <span
              className="an-ranked__fill"
              style={{ width: `${Math.max(1.5, (r[valueKey] / max) * 100)}%`, background: r.color || color }}
            />
          </span>
          <span className="an-ranked__val">{format(r[valueKey])}</span>
          {r.meta && <span className="an-ranked__meta">{r.meta}</span>}
        </li>
      ))}
    </ul>
  );
};

/* Star rating readout — the number leads, stars reinforce it. */
const Stars = ({ value, size = 13 }) => {
  if (value == null) return <span className="an-stars an-stars--none">Not rated yet</span>;
  return (
    <span className="an-stars" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={size}
          aria-hidden="true"
          className={value >= s - 0.25 ? 'is-on' : 'is-off'}
          fill={value >= s - 0.25 ? 'currentColor' : 'none'}
        />
      ))}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════════
   FILTER CONTROLS  (one row, above everything they scope)
═══════════════════════════════════════════════════════════════ */
const Dropdown = ({ label, value, children, icon: Icon }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div className="an-dd" ref={ref}>
      <button
        type="button"
        className={`an-dd__btn ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title={typeof value === 'string' ? value : undefined}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {Icon && <Icon size={15} aria-hidden="true" />}
        <span className="an-dd__label">{label}</span>
        <span className="an-dd__value">{value}</span>
        <ChevronDown size={15} aria-hidden="true" className="an-dd__chev" />
      </button>
      {open && (
        <div className="an-dd__menu" role="listbox">
          {typeof children === 'function' ? children(() => setOpen(false)) : children}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   OVERVIEW
═══════════════════════════════════════════════════════════════ */
/* Current vs previous on ONE axis, aligned day-for-day. Previous is
   context, so it takes the de-emphasis grey, not a second hue. */
const buildTrend = (cur, prev) => {
  const prevTimeline = prev?.timeline || [];
  return (cur?.timeline || []).map((d, i) => ({
    date: d.date,
    revenue: d.revenue,
    orders: d.orders,
    prevRevenue: prevTimeline[i]?.revenue ?? null,
    prevOrders:  prevTimeline[i]?.orders ?? null,
  }));
};

const TREND_METRICS = {
  revenue: { label: 'Revenue', title: 'Revenue trend', sub: 'Daily net sales', tick: money, full: moneyFull, axisW: 58 },
  orders:  { label: 'Orders',  title: 'Orders trend',  sub: 'Daily fulfilled orders', tick: compact, full: n0, axisW: 40 },
};

const OverviewTab = ({ cur, prev, range }) => {
  const [metric, setMetric] = useState('revenue');
  const trend = useMemo(() => buildTrend(cur, prev), [cur, prev]);
  const M = TREND_METRICS[metric];
  const prevKey = metric === 'revenue' ? 'prevRevenue' : 'prevOrders';
  // A one- or two-day range has no line to draw, so show the points.
  const dots = trend.length < 3 ? { r: 3.5, fill: SERIES[0], strokeWidth: 0 } : false;

  const orderTypes = (cur.orderTypes || []).filter((t) => t.revenue > 0);
  const typeTotal = orderTypes.reduce((s, t) => s + t.revenue, 0);

  const methods = (cur.paymentMethods || []).filter((m) => m.revenue > 0).slice(0, 8);

  // Peak-hours grid: trim to hours the kitchen actually trades in,
  // so the heatmap isn't mostly dead cells.
  const heat = useMemo(() => cur.heatmap || [], [cur.heatmap]);
  const activeHours = useMemo(() => {
    const live = new Set(heat.filter((c) => c.orders > 0).map((c) => c.hour));
    if (!live.size) return [];
    const lo = Math.min(...live), hi = Math.max(...live);
    return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
  }, [heat]);

  const heatMax = Math.max(...heat.map((c) => c.orders), 1);
  const heatIndex = useMemo(() => {
    const m = new Map();
    heat.forEach((c) => m.set(`${c.dow}-${c.hour}`, c));
    return m;
  }, [heat]);

  const heatColor = (orders) => {
    if (!orders) return HEAT[0];
    const step = Math.ceil((orders / heatMax) * (HEAT.length - 1));
    return HEAT[Math.min(HEAT.length - 1, Math.max(1, step))];
  };

  const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dowIdx = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };

  const dishes = (cur.dishes || []).slice(0, 8);

  return (
    <>
      <div className="an-grid">
        <ChartCard
          wide
          title={M.title}
          subtitle={`${M.sub} · ${range.label.toLowerCase()} against the previous ${range.days} days`}
          actions={
            <div className="an-toggle an-toggle--text" role="group" aria-label="Trend metric">
              {Object.entries(TREND_METRICS).map(([k, m]) => (
                <button
                  key={k}
                  type="button"
                  className={metric === k ? 'is-active' : ''}
                  aria-pressed={metric === k}
                  onClick={() => setMetric(k)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          }
          table={{
            columns: [
              { key: 'date', label: 'Date', format: dayLabel },
              { key: 'revenue', label: 'Revenue', align: 'right', format: moneyFull },
              { key: 'prevRevenue', label: 'Prev. revenue', align: 'right', format: (v) => (v == null ? '—' : moneyFull(v)) },
              { key: 'orders', label: 'Orders', align: 'right', format: n0 },
              { key: 'prevOrders', label: 'Prev. orders', align: 'right', format: (v) => (v == null ? '—' : n0(v)) },
            ],
            rows: trend.map((d) => ({ ...d, key: d.date })),
          }}
        >
          {trend.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trend} margin={{ top: 8, right: 14, left: 4, bottom: 4 }}>
                <CartesianGrid stroke={INK.grid} strokeWidth={1} vertical={false} />
                <XAxis
                  dataKey="date" tickFormatter={dayLabel} tickLine={false}
                  axisLine={{ stroke: INK.axis }} tick={{ fill: INK.muted, fontSize: 11 }}
                  minTickGap={24}
                />
                <YAxis
                  tickFormatter={M.tick} tickLine={false} axisLine={false}
                  tick={{ fill: INK.muted, fontSize: 11 }} width={M.axisW} allowDecimals={false}
                />
                <Tooltip
                  cursor={{ stroke: INK.axis, strokeWidth: 1 }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0].payload;
                    const other = metric === 'revenue'
                      ? { label: 'Orders', value: n0(p.orders) }
                      : { label: 'Revenue', value: moneyFull(p.revenue) };
                    return (
                      <TipBox
                        title={dayLabel(label)}
                        rows={[
                          { label: M.label, value: M.full(p[metric]), color: SERIES[0] },
                          { label: 'Previous period', value: p[prevKey] == null ? '—' : M.full(p[prevKey]), color: INK.context },
                          other,
                        ]}
                      />
                    );
                  }}
                />
                <Legend
                  verticalAlign="top" align="right" height={28} iconType="plainline"
                  formatter={(v) => <span className="an-rc-legend">{v}</span>}
                />
                <Line
                  type="monotone" dataKey={prevKey} name="Previous period"
                  stroke={INK.context} strokeWidth={2} dot={false} activeDot={false}
                  connectNulls isAnimationActive={false}
                />
                <Line
                  type="monotone" dataKey={metric} name="This period"
                  stroke={SERIES[0]} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
                  dot={dots}
                  activeDot={{ r: 5, fill: SERIES[0], stroke: INK.surface, strokeWidth: 2 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyState title="No sales recorded in this period" />}
        </ChartCard>

        <ChartCard
          title="Where the money comes from"
          subtitle="Revenue by order type"
          table={{
            columns: [
              { key: 'type', label: 'Order type', format: titleCase },
              { key: 'orders', label: 'Orders', align: 'right', format: n0 },
              { key: 'revenue', label: 'Revenue', align: 'right', format: moneyFull },
            ],
            rows: orderTypes.map((t) => ({ ...t, key: t.type })),
          }}
        >
          <ShareBar
            total={typeTotal}
            formatValue={money}
            segments={orderTypes.map((t, i) => ({
              label: titleCase(t.type),
              value: t.revenue,
              color: SERIES[i % 3], // capped at 3 — all-pairs safe
            }))}
          />
        </ChartCard>

        <ChartCard
          title="How guests pay"
          subtitle="Revenue by payment method"
          table={{
            columns: [
              { key: 'method', label: 'Method' },
              { key: 'orders', label: 'Orders', align: 'right', format: n0 },
              { key: 'revenue', label: 'Revenue', align: 'right', format: moneyFull },
            ],
            rows: methods.map((m) => ({ ...m, key: m.method })),
          }}
        >
          <RankedBars
            rows={methods.map((m) => ({
              key: m.method, label: m.method, value: m.revenue, meta: `${n0(m.orders)} orders`,
            }))}
            format={money}
            emptyText="No payments recorded yet"
          />
        </ChartCard>

        <ChartCard
          wide
          title="When you are busiest"
          subtitle="Orders by weekday and hour — darker means busier"
          table={{
            columns: [
              { key: 'hour', label: 'Hour', format: hourLabel },
              ...DOW.map((d) => ({ key: d, label: d, align: 'right', format: n0 })),
            ],
            rows: activeHours.map((h) => {
              const row = { key: h, hour: h };
              DOW.forEach((d) => { row[d] = heatIndex.get(`${dowIdx[d]}-${h}`)?.orders || 0; });
              return row;
            }),
          }}
        >
          {activeHours.length ? (
            <div className="an-heatwrap">
              <div className="an-heat" style={{ gridTemplateColumns: `44px repeat(${activeHours.length}, minmax(26px, 1fr))` }}>
                <span className="an-heat__corner" />
                {activeHours.map((h) => (
                  <span key={`h${h}`} className="an-heat__colhead">{hourLabel(h)}</span>
                ))}
                {DOW.map((d) => (
                  <React.Fragment key={d}>
                    <span className="an-heat__rowhead">{d}</span>
                    {activeHours.map((h) => {
                      const cell = heatIndex.get(`${dowIdx[d]}-${h}`) || { orders: 0, revenue: 0 };
                      return (
                        <span
                          key={`${d}-${h}`}
                          className="an-heat__cell"
                          style={{ background: heatColor(cell.orders) }}
                          title={`${d} ${hourLabel(h)} · ${n0(cell.orders)} orders · ${moneyFull(cell.revenue)}`}
                        />
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
              <div className="an-heat__scale">
                <span>Quiet</span>
                {HEAT.map((c) => <i key={c} style={{ background: c }} aria-hidden="true" />)}
                <span>Busiest ({n0(heatMax)} orders)</span>
              </div>
            </div>
          ) : <EmptyState icon={Clock} title="Not enough orders to find a pattern yet" />}
        </ChartCard>

        <ChartCard
          wide
          title="Best selling dishes"
          subtitle="Net revenue after item discounts"
          table={{
            columns: [
              { key: 'name', label: 'Dish' },
              { key: 'qty', label: 'Sold', align: 'right', format: n0 },
              { key: 'revenue', label: 'Revenue', align: 'right', format: moneyFull },
              { key: 'outletCount', label: 'Outlets', align: 'right', format: n0 },
            ],
            rows: (cur.dishes || []).map((d) => ({ ...d, key: d.name })),
          }}
        >
          <RankedBars
            rows={dishes.map((d) => ({
              key: d.name, label: d.name, value: d.revenue, meta: `${n0(d.qty)} sold`,
            }))}
            format={money}
            emptyText="No dishes sold in this period"
            columns
          />
        </ChartCard>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════
   OUTLETS  —  which restaurant is actually carrying the group
═══════════════════════════════════════════════════════════════ */
const OutletsTab = ({ cur, prev, colorOf, restaurants, onFocus }) => {
  const prevById = useMemo(() => {
    const m = new Map();
    (prev?.perRestaurant || []).forEach((r) => m.set(r.restaurantId, r));
    return m;
  }, [prev]);

  const ratingById = useMemo(() => {
    const m = new Map();
    (cur.ratings?.perRestaurant || []).forEach((r) => m.set(String(r.restaurantId), r));
    return m;
  }, [cur]);

  const rows = useMemo(() => (
    (cur.perRestaurant || [])
      .map((r) => {
        const p = prevById.get(r.restaurantId);
        return {
          ...r,
          key: r.restaurantId,
          prevRevenue: p?.revenue ?? null,
          prevOrders:  p?.orders ?? null,
          growthPct:   growth(r.revenue, p?.revenue),
          rating:      ratingById.get(String(r.restaurantId))?.experience ?? null,
          share:       0,
        };
      })
      .sort((a, b) => b.revenue - a.revenue)
  ), [cur, prevById, ratingById]);

  const total = rows.reduce((s, r) => s + r.revenue, 0);
  rows.forEach((r) => { r.share = total ? (r.revenue / total) * 100 : 0; });

  /* Daily revenue per outlet. Colour follows the outlet, not its
     rank, and the tail past 8 outlets folds into "Other" rather
     than inventing a ninth hue. */
  const tracked = rows.slice(0, MAX_SERIES);
  const rest    = rows.slice(MAX_SERIES);
  const multi   = tracked.length > 1;

  const series = useMemo(() => (cur.timeline || []).map((d) => {
    const row = { date: d.date };
    tracked.forEach((r) => { row[r.restaurantId] = d[r.restaurantId] || 0; });
    if (rest.length) {
      row.__other = rest.reduce((s, r) => s + (d[r.restaurantId] || 0), 0);
    }
    return row;
  }), [cur.timeline, tracked, rest]);

  if (!rows.length) {
    return <EmptyState icon={Store} title="No outlets to compare" body="Add a property to start tracking performance." />;
  }

  return (
    <div className="an-grid">
      <ChartCard
        wide
        title="Outlet leaderboard"
        subtitle="Ranked by net revenue this period"
        table={{
          columns: [
            { key: 'name', label: 'Outlet' },
            { key: 'revenue', label: 'Revenue', align: 'right', format: moneyFull },
            { key: 'share', label: 'Share', align: 'right', format: pct1 },
            { key: 'orders', label: 'Orders', align: 'right', format: n0 },
            { key: 'avgOrderValue', label: 'Avg order', align: 'right', format: moneyFull },
            { key: 'cancellationRate', label: 'Cancelled', align: 'right', format: pct1 },
            { key: 'growthPct', label: 'Growth', align: 'right', format: (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`) },
          ],
          rows,
        }}
      >
        <div className="an-outlets">
          {rows.map((r) => (
            <button
              type="button"
              key={r.restaurantId}
              className="an-outlet"
              onClick={() => onFocus(r.restaurantId)}
              title={`Focus analytics on ${r.name}`}
            >
              <span className="an-outlet__dot" style={{ background: colorOf(r.restaurantId) }} aria-hidden="true" />
              <span className="an-outlet__main">
                <span className="an-outlet__name">{r.name}</span>
                <span className="an-outlet__track">
                  <span
                    className="an-outlet__fill"
                    style={{ width: `${Math.max(1.5, r.share)}%`, background: colorOf(r.restaurantId) }}
                  />
                </span>
                <span className="an-outlet__meta">
                  {n0(r.orders)} orders · {moneyFull(r.avgOrderValue)} avg
                  {r.rating != null && <> · {r.rating.toFixed(1)}★</>}
                  {r.cancellationRate > 0 && <> · {pct1(r.cancellationRate)} cancelled</>}
                </span>
              </span>
              <span className="an-outlet__right">
                <span className="an-outlet__rev"><Figure value={r.revenue} money max={12} /></span>
                <span className="an-outlet__share">{pct1(r.share)} of group</span>
                <Delta curr={r.revenue} prev={r.prevRevenue} />
              </span>
            </button>
          ))}
        </div>
      </ChartCard>

      {multi && (
        <ChartCard
          wide
          title="Outlet revenue over time"
          subtitle="Each outlet keeps its colour across every view"
          table={{
            columns: [
              { key: 'date', label: 'Date', format: dayLabel },
              ...tracked.map((r) => ({ key: r.restaurantId, label: r.name, align: 'right', format: moneyFull })),
              ...(rest.length ? [{ key: '__other', label: `Other (${rest.length})`, align: 'right', format: moneyFull }] : []),
            ],
            rows: series,
          }}
        >
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={series} margin={{ top: 8, right: 14, left: 4, bottom: 4 }}>
              <CartesianGrid stroke={INK.grid} strokeWidth={1} vertical={false} />
              <XAxis
                dataKey="date" tickFormatter={dayLabel} tickLine={false}
                axisLine={{ stroke: INK.axis }} tick={{ fill: INK.muted, fontSize: 11 }} minTickGap={24}
              />
              <YAxis
                tickFormatter={money} tickLine={false} axisLine={false}
                tick={{ fill: INK.muted, fontSize: 11 }} width={58}
              />
              <Tooltip
                cursor={{ stroke: INK.axis, strokeWidth: 1 }}
                content={({ active, payload, label }) => (!active || !payload?.length) ? null : (
                  <TipBox
                    title={dayLabel(label)}
                    rows={payload
                      .slice()
                      .sort((a, b) => b.value - a.value)
                      .map((p) => ({ label: p.name, value: moneyFull(p.value), color: p.color }))}
                  />
                )}
              />
              <Legend
                verticalAlign="top" align="left" height={30} iconType="plainline"
                formatter={(v) => <span className="an-rc-legend">{v}</span>}
              />
              {tracked.map((r) => (
                <Line
                  key={r.restaurantId}
                  type="monotone"
                  dataKey={r.restaurantId}
                  name={r.name}
                  stroke={colorOf(r.restaurantId)}
                  strokeWidth={2} strokeLinecap="round" dot={false}
                  activeDot={{ r: 4.5, stroke: INK.surface, strokeWidth: 2 }}
                  isAnimationActive={false}
                />
              ))}
              {rest.length > 0 && (
                <Line
                  type="monotone" dataKey="__other" name={`Other (${rest.length})`}
                  stroke={INK.context} strokeWidth={2} dot={false} isAnimationActive={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <ChartCard
        wide
        title="Money still to collect"
        subtitle="Fulfilled orders whose payment is pending or on account"
        table={{
          columns: [
            { key: 'name', label: 'Outlet' },
            { key: 'collected', label: 'Collected', align: 'right', format: moneyFull },
            { key: 'pending', label: 'Pending', align: 'right', format: moneyFull },
            { key: 'due', label: 'On account', align: 'right', format: moneyFull },
            { key: 'outstanding', label: 'Outstanding', align: 'right', format: moneyFull },
          ],
          rows: rows.map((r) => ({
            key: r.restaurantId, name: r.name,
            collected: r.payment.collected.amount,
            pending: r.payment.pending.amount,
            due: r.payment.due.amount,
            outstanding: r.outstanding,
          })),
        }}
      >
        {rows.some((r) => r.outstanding > 0) ? (
          <RankedBars
            rows={rows
              .filter((r) => r.outstanding > 0)
              .sort((a, b) => b.outstanding - a.outstanding)
              .map((r) => ({
                key: r.restaurantId,
                label: r.name,
                value: r.outstanding,
                meta: `${n0(r.payment.pending.orders + r.payment.due.orders)} orders`,
              }))}
            format={money}
            color={STATUS.bad}
            columns
          />
        ) : (
          <EmptyState icon={Wallet} title="Everything is collected" body="No fulfilled order is waiting on payment." />
        )}
      </ChartCard>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   GUESTS
═══════════════════════════════════════════════════════════════ */
const GuestsTab = ({ cur, prev }) => {
  const c = cur.customers || {};
  const p = prev?.customers || {};

  const mix = [
    { label: 'First-time guests', value: c.newCustomers || 0, color: SERIES[0] },
    { label: 'Returning guests',  value: c.returningCustomers || 0, color: SERIES[1] },
  ];
  const mixTotal = mix.reduce((s, m) => s + m.value, 0);

  if (!c.customers) {
    return (
      <EmptyState
        icon={Users}
        title="No identified guests in this period"
        body="Guest analytics build up as orders are placed against a customer profile."
      />
    );
  }

  return (
    <div className="an-grid">
      <div className="an-tilerow an-tilerow--inner">
        <StatTile
          label="Guests served" icon={Users}
          value={<Figure value={c.customers} />} curr={c.customers} prev={p.customers}
        />
        <StatTile
          label="Repeat rate" icon={RefreshCw}
          value={pct1(c.repeatRate)} curr={c.repeatRate} prev={p.repeatRate}
          hint="Share who had ordered before"
        />
        <StatTile
          label="Revenue per guest" icon={Wallet}
          value={<Figure value={c.revenuePerCustomer} money />} curr={c.revenuePerCustomer} prev={p.revenuePerCustomer}
        />
        <StatTile
          label="Visits per guest" icon={ShoppingBag}
          value={Number(c.visitsPerCustomer || 0).toFixed(2)}
          curr={c.visitsPerCustomer} prev={p.visitsPerCustomer}
        />
      </div>

      <ChartCard
        title="New against returning"
        subtitle="Guests who ordered in this period"
        table={{
          columns: [
            { key: 'label', label: 'Guest type' },
            { key: 'value', label: 'Guests', align: 'right', format: n0 },
          ],
          rows: mix.map((m) => ({ ...m, key: m.label })),
        }}
      >
        <ShareBar segments={mix} total={mixTotal} />
        <p className="an-note">
          Returning guests brought in <strong>{moneyFull(c.returningRevenue)}</strong> — {pct1(c.returningRevenuePct)} of
          revenue in this period.
          {c.multiOutletGuests > 0 && <> {n0(c.multiOutletGuests)} guests ordered at more than one of your outlets.</>}
        </p>
      </ChartCard>

      <ChartCard
        title="How often guests come back"
        subtitle="Visits inside this period"
        table={{
          columns: [
            { key: 'label', label: 'Visits' },
            { key: 'customers', label: 'Guests', align: 'right', format: n0 },
          ],
          rows: (c.frequency || []).map((f) => ({ ...f, key: f.label })),
        }}
      >
        <RankedBars
          rows={(c.frequency || []).map((f) => ({ key: f.label, label: f.label, value: f.customers }))}
          format={n0}
          emptyText="No repeat visits recorded yet"
        />
      </ChartCard>

      <ChartCard
        wide
        title="Your most valuable guests"
        subtitle="By spend in this period, with lifetime value alongside"
      >
        {(c.top || []).length ? (
          <div className="an-tablewrap">
            <table className="an-table">
              <thead>
                <tr>
                  <th scope="col">Guest</th>
                  <th scope="col">Phone</th>
                  <th scope="col" className="ta-r">Orders</th>
                  <th scope="col" className="ta-r">Spend</th>
                  <th scope="col" className="ta-r">Lifetime orders</th>
                  <th scope="col" className="ta-r">Lifetime spend</th>
                </tr>
              </thead>
              <tbody>
                {c.top.map((t) => (
                  <tr key={t.customerId}>
                    <td>{t.name || 'Guest'}</td>
                    <td className="an-mono">{t.phone || '—'}</td>
                    <td className="ta-r">{n0(t.orders)}</td>
                    <td className="ta-r">{moneyFull(t.spend)}</td>
                    <td className="ta-r">{n0(t.lifetimeOrders)}</td>
                    <td className="ta-r">{moneyFull(t.lifetimeSpend)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <EmptyState icon={Users} title="No guest records in this period" />}
      </ChartCard>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   REPUTATION
═══════════════════════════════════════════════════════════════ */
const RATING_FIELDS = [
  { key: 'food',        label: 'Food quality' },
  { key: 'staff',       label: 'Staff behaviour' },
  { key: 'cleanliness', label: 'Cleanliness' },
  { key: 'pricing',     label: 'Value for money' },
  { key: 'experience',  label: 'Overall experience' },
];

const ReputationTab = ({ cur, prev }) => {
  const r = cur.ratings || {};
  const pr = prev?.ratings || {};
  const avgs = r.averages || {};

  const distTotal = (r.distribution || []).reduce((s, d) => s + d.count, 0);

  if (!r.count) {
    return (
      <EmptyState
        icon={Star}
        title="No ratings in this period"
        body="Ratings appear here once guests review a completed order."
      />
    );
  }

  return (
    <div className="an-grid">
      <ChartCard
        title="What guests rate you on"
        subtitle={`Average of ${n0(r.count)} ratings, out of 5`}
        table={{
          columns: [
            { key: 'label', label: 'Category' },
            { key: 'value', label: 'Average', align: 'right', format: (v) => (v == null ? '—' : v.toFixed(2)) },
          ],
          rows: RATING_FIELDS.map((f) => ({ key: f.key, label: f.label, value: avgs[f.key] })),
        }}
      >
        <ul className="an-ratings">
          {RATING_FIELDS.map((f) => {
            const v = avgs[f.key];
            const pv = pr.averages?.[f.key];
            return (
              <li key={f.key}>
                <span className="an-ratings__label">{f.label}</span>
                <span className="an-ratings__track">
                  <span
                    className="an-ratings__fill"
                    style={{ width: `${((v || 0) / 5) * 100}%`, background: SERIES[0] }}
                  />
                </span>
                <span className="an-ratings__val">{v == null ? '—' : v.toFixed(2)}</span>
                <span className="an-ratings__delta">
                  {v != null && pv != null ? <Delta curr={v} prev={pv} /> : null}
                </span>
              </li>
            );
          })}
        </ul>
      </ChartCard>

      <ChartCard
        title="Rating spread"
        subtitle="Overall experience scores given"
        table={{
          columns: [
            { key: 'star', label: 'Stars', format: (v) => `${v}★` },
            { key: 'count', label: 'Ratings', align: 'right', format: n0 },
          ],
          rows: (r.distribution || []).map((d) => ({ ...d, key: d.star })),
        }}
      >
        <ul className="an-ranked an-ranked--stars">
          {(r.distribution || []).slice().reverse().map((d) => (
            <li key={d.star}>
              <span className="an-ranked__label an-ranked__label--star">{d.star}★</span>
              <span className="an-ranked__track">
                <span
                  className="an-ranked__fill"
                  style={{
                    width: `${distTotal ? Math.max(0.5, (d.count / distTotal) * 100) : 0}%`,
                    background: SERIES[0],
                  }}
                />
              </span>
              <span className="an-ranked__val">{n0(d.count)}</span>
              <span className="an-ranked__meta">{distTotal ? pct1((d.count / distTotal) * 100) : '0%'}</span>
            </li>
          ))}
        </ul>
      </ChartCard>

      {(r.perRestaurant || []).length > 1 && (
        <ChartCard
          wide
          title="Ratings by outlet"
          subtitle="Where experience is strongest and where it slips"
          table={{
            columns: [
              { key: 'name', label: 'Outlet' },
              { key: 'count', label: 'Ratings', align: 'right', format: n0 },
              ...RATING_FIELDS.map((f) => ({
                key: f.key, label: f.label, align: 'right',
                format: (v) => (v == null ? '—' : v.toFixed(2)),
              })),
            ],
            rows: r.perRestaurant.map((x) => ({ ...x, key: String(x.restaurantId) })),
          }}
        >
          <div className="an-tablewrap">
            <table className="an-table">
              <thead>
                <tr>
                  <th scope="col">Outlet</th>
                  <th scope="col" className="ta-r">Ratings</th>
                  <th scope="col">Overall</th>
                  {RATING_FIELDS.filter((f) => f.key !== 'experience').map((f) => (
                    <th key={f.key} scope="col" className="ta-r">{f.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {r.perRestaurant
                  .slice()
                  .sort((a, b) => (b.experience || 0) - (a.experience || 0))
                  .map((x) => (
                    <tr key={String(x.restaurantId)}>
                      <td>{x.name}</td>
                      <td className="ta-r">{n0(x.count)}</td>
                      <td>
                        <span className="an-ratecell">
                          <strong>{x.experience == null ? '—' : x.experience.toFixed(1)}</strong>
                          <Stars value={x.experience} />
                        </span>
                      </td>
                      {RATING_FIELDS.filter((f) => f.key !== 'experience').map((f) => (
                        <td key={f.key} className="ta-r">{x[f.key] == null ? '—' : x[f.key].toFixed(2)}</td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      )}

      <ChartCard wide title="What guests actually said" subtitle="Most recent written feedback">
        {(r.recent || []).length ? (
          <ul className="an-feedback">
            {r.recent.map((f) => (
              <li key={f._id}>
                <div className="an-feedback__head">
                  <span className="an-ratecell">
                    <strong>{f.experience ?? '—'}</strong>
                    <Stars value={f.experience} size={12} />
                  </span>
                  <span className="an-feedback__meta">{f.name} · {timeAgo(f.createdAt)}</span>
                </div>
                <p className="an-feedback__text">{f.comment}</p>
              </li>
            ))}
          </ul>
        ) : <EmptyState icon={MessageCircle} title="No written feedback in this period" />}
      </ChartCard>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   REACH  —  profile performance & guest-created moments
═══════════════════════════════════════════════════════════════ */
const ReachTab = ({ cur, prev, restaurants }) => {
  const e  = cur.engagement || {};
  const pe = prev?.engagement || {};
  const m  = cur.moments || {};
  const pm = prev?.moments || {};

  const lifetime = restaurants.reduce((acc, r) => ({
    likes:    acc.likes    + (r.lifetime?.likes || 0),
    views:    acc.views    + (r.lifetime?.views || 0),
    shares:   acc.shares   + (r.lifetime?.shares || 0),
    comments: acc.comments + (r.lifetime?.comments || 0),
  }), { likes: 0, views: 0, shares: 0, comments: 0 });

  const timeline = e.timeline || [];
  const hasActivity = timeline.some((d) => d.likes || d.shares || d.comments);

  return (
    <div className="an-grid">
      <div className="an-tilerow an-tilerow--inner">
        <StatTile label="Profile likes" icon={Heart} value={<Figure value={e.likes} />} curr={e.likes} prev={pe.likes} />
        <StatTile label="Shares" icon={Share2} value={<Figure value={e.shares} />} curr={e.shares} prev={pe.shares} />
        <StatTile label="Comments" icon={MessageCircle} value={<Figure value={e.comments} />} curr={e.comments} prev={pe.comments} />
        <StatTile label="Guest moments" icon={Camera} value={<Figure value={m.posts} />} curr={m.posts} prev={pm.posts} />
      </div>

      <ChartCard
        wide
        title="Engagement over time"
        subtitle="Likes, shares and comments on your restaurant profiles"
        table={{
          columns: [
            { key: 'date', label: 'Date', format: dayLabel },
            { key: 'likes', label: 'Likes', align: 'right', format: n0 },
            { key: 'shares', label: 'Shares', align: 'right', format: n0 },
            { key: 'comments', label: 'Comments', align: 'right', format: n0 },
          ],
          rows: timeline.map((d) => ({ ...d, key: d.date })),
        }}
      >
        {hasActivity ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={timeline} margin={{ top: 8, right: 14, left: 4, bottom: 4 }}>
              <CartesianGrid stroke={INK.grid} strokeWidth={1} vertical={false} />
              <XAxis
                dataKey="date" tickFormatter={dayLabel} tickLine={false}
                axisLine={{ stroke: INK.axis }} tick={{ fill: INK.muted, fontSize: 11 }} minTickGap={24}
              />
              <YAxis
                tickFormatter={compact} tickLine={false} axisLine={false}
                tick={{ fill: INK.muted, fontSize: 11 }} width={34} allowDecimals={false}
              />
              <Tooltip
                cursor={{ stroke: INK.axis, strokeWidth: 1 }}
                content={({ active, payload, label }) => (!active || !payload?.length) ? null : (
                  <TipBox
                    title={dayLabel(label)}
                    rows={payload.map((p) => ({ label: p.name, value: n0(p.value), color: p.color }))}
                  />
                )}
              />
              <Legend
                verticalAlign="top" align="left" height={30} iconType="plainline"
                formatter={(v) => <span className="an-rc-legend">{v}</span>}
              />
              <Line type="monotone" dataKey="likes"    name="Likes"    stroke={SERIES[0]} strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="shares"   name="Shares"   stroke={SERIES[1]} strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="comments" name="Comments" stroke={SERIES[2]} strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState
            icon={Heart}
            title="No profile engagement in this period"
            body="Likes, shares and comments on your listing will show up here."
          />
        )}
      </ChartCard>

      <ChartCard
        title="All-time profile reach"
        subtitle="Running totals across your listings"
        table={{
          columns: [
            { key: 'name', label: 'Outlet' },
            { key: 'views', label: 'Views', align: 'right', format: n0 },
            { key: 'likes', label: 'Likes', align: 'right', format: n0 },
            { key: 'shares', label: 'Shares', align: 'right', format: n0 },
            { key: 'comments', label: 'Comments', align: 'right', format: n0 },
          ],
          rows: restaurants.map((r) => ({
            key: String(r._id), name: r.name,
            views: r.lifetime?.views || 0,
            likes: r.lifetime?.likes || 0,
            shares: r.lifetime?.shares || 0,
            comments: r.lifetime?.comments || 0,
          })),
        }}
      >
        <div className="an-reach">
          {[
            { label: 'Profile views', value: lifetime.views, icon: Eye },
            { label: 'Likes', value: lifetime.likes, icon: Heart },
            { label: 'Shares', value: lifetime.shares, icon: Share2 },
            { label: 'Comments', value: lifetime.comments, icon: MessageCircle },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="an-reach__item">
              <Icon size={16} aria-hidden="true" />
              <span className="an-reach__val">{compact(value)}</span>
              <span className="an-reach__label">{label}</span>
            </div>
          ))}
        </div>
      </ChartCard>

      <ChartCard
        title="Moments by outlet"
        subtitle="Photos guests posted in this period"
        table={{
          columns: [
            { key: 'name', label: 'Outlet' },
            { key: 'posts', label: 'Moments', align: 'right', format: n0 },
            { key: 'likes', label: 'Likes', align: 'right', format: n0 },
          ],
          rows: (m.perRestaurant || []).map((x) => ({ ...x, key: String(x.restaurantId) })),
        }}
      >
        <RankedBars
          rows={(m.perRestaurant || [])
            .slice()
            .sort((a, b) => b.posts - a.posts)
            .map((x) => ({
              key: String(x.restaurantId), label: x.name, value: x.posts, meta: `${n0(x.likes)} likes`,
            }))}
          format={n0}
          emptyText="No guest moments in this period"
        />
      </ChartCard>

      {(m.top || []).length > 0 && (
        <ChartCard wide title="Moments that travelled furthest" subtitle="Most-liked guest photos in this period">
          <ul className="an-moments">
            {m.top.map((x) => (
              <li key={x._id} className="an-moment">
                <div className="an-moment__img">
                  {/* A moment's image may 404 — drop the broken icon and
                      leave the placeholder surface rather than a torn card. */}
                  {x.imageurl && (
                    <img
                      src={x.imageurl}
                      alt={x.caption || `Guest moment at ${x.name}`}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                </div>
                <div className="an-moment__body">
                  <span className="an-moment__likes"><Heart size={12} aria-hidden="true" />{n0(x.likes)}</span>
                  <span className="an-moment__name">{x.name}</span>
                  {x.caption && <p className="an-moment__cap">{x.caption}</p>}
                </div>
              </li>
            ))}
          </ul>
        </ChartCard>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   INSIGHTS  —  the plain-language read of the numbers
═══════════════════════════════════════════════════════════════ */
const buildInsights = (cur, prev, range) => {
  const out = [];
  if (!cur) return out;

  const t = cur.totals || {};
  const p = prev?.totals || {};
  const c = cur.customers || {};

  const revG = growth(t.revenue, p.revenue);
  if (revG !== null && Math.abs(revG) >= 1) {
    out.push({
      tone: revG > 0 ? 'good' : 'bad',
      text: `Revenue is ${revG > 0 ? 'up' : 'down'} ${Math.abs(revG).toFixed(1)}% on the previous ${range.days} days, at ${moneyFull(t.revenue)}.`,
    });
  }

  const aovG = growth(t.avgOrderValue, p.avgOrderValue);
  const ordG = growth(t.orders, p.orders);
  if (revG !== null && ordG !== null && aovG !== null) {
    // Separating volume from basket size is the question owners
    // actually ask when revenue moves.
    if (revG > 1 && ordG > 1 && aovG <= 0) {
      out.push({ tone: 'info', text: `Growth is coming from more orders (${ordG > 0 ? '+' : ''}${ordG.toFixed(1)}%), not bigger bills — average order value is ${aovG.toFixed(1)}%.` });
    } else if (revG > 1 && aovG > 1 && ordG <= 0) {
      out.push({ tone: 'info', text: `Guests are spending more per visit (+${aovG.toFixed(1)}% average order) even though order count is ${ordG.toFixed(1)}%.` });
    }
  }

  if (t.outstanding > 0) {
    out.push({
      tone: 'warn',
      text: `${moneyFull(t.outstanding)} across ${n0(t.payment.pending.orders + t.payment.due.orders)} fulfilled orders is still uncollected.`,
    });
  }

  if (t.cancellationRate >= 5) {
    out.push({ tone: 'bad', text: `${pct1(t.cancellationRate)} of orders were cancelled — worth checking against kitchen capacity at peak hours.` });
  }

  if (c.customers) {
    out.push({
      tone: c.repeatRate >= 30 ? 'good' : 'info',
      text: `${pct1(c.repeatRate)} of guests had ordered with you before, and returning guests drove ${pct1(c.returningRevenuePct)} of revenue.`,
    });
  }

  const best = (cur.perRestaurant || []).slice().sort((a, b) => b.revenue - a.revenue)[0];
  if (best && (cur.perRestaurant || []).length > 1 && t.revenue > 0) {
    out.push({
      tone: 'info',
      text: `${best.name} is your strongest outlet this period at ${pct1((best.revenue / t.revenue) * 100)} of group revenue.`,
    });
  }

  const topHour = (cur.hourly || []).slice().sort((a, b) => b.orders - a.orders)[0];
  if (topHour?.orders) {
    out.push({ tone: 'info', text: `Your busiest hour is ${hourLabel(topHour.hour)}–${hourLabel((topHour.hour + 1) % 24)}, taking ${n0(topHour.orders)} orders.` });
  }

  const rating = cur.ratings?.averages?.experience;
  if (rating != null) {
    const weakest = RATING_FIELDS
      .filter((f) => f.key !== 'experience' && cur.ratings.averages[f.key] != null)
      .sort((a, b) => cur.ratings.averages[a.key] - cur.ratings.averages[b.key])[0];
    out.push({
      tone: rating >= 4 ? 'good' : rating >= 3 ? 'info' : 'bad',
      text: `Guests rate you ${rating.toFixed(1)}/5 overall${weakest ? `, with ${weakest.label.toLowerCase()} the lowest at ${cur.ratings.averages[weakest.key].toFixed(1)}` : ''}.`,
    });
  }

  return out;
};

/* ═══════════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════════ */
const TABS = [
  { key: 'overview',   label: 'Overview',   icon: BarChart3 },
  { key: 'outlets',    label: 'Outlets',    icon: Store },
  { key: 'guests',     label: 'Guests',     icon: Users },
  { key: 'reputation', label: 'Reputation', icon: Star },
  { key: 'reach',      label: 'Reach',      icon: Heart },
];

const TONE_RANK = { bad: 0, warn: 1, good: 2, info: 3 };
const INSIGHTS_VISIBLE = 3;

const spanLabel = (from, to) => (from === to ? dayLabel(from) : `${dayLabel(from)} – ${dayLabel(to)}`);

/* Revenue shape inside the hero — current in full ink, previous as
   a faint ghost. Strokes use currentColor so CSS owns the colour. */
const Sparkline = ({ data }) => (
  <div className="an-hero__spark" aria-hidden="true">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 4, right: 2, left: 2, bottom: 2 }}>
        <XAxis dataKey="date" hide />
        <YAxis hide domain={[0, 'auto']} />
        <Line
          type="monotone" dataKey="prevRevenue" stroke="currentColor" strokeOpacity={0.35}
          strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false}
        />
        <Line
          type="monotone" dataKey="revenue" stroke="currentColor"
          strokeWidth={2} dot={false} isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const Analytics = () => {
  const [rangeKey, setRangeKey]   = useState('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo]     = useState('');
  const [scope, setScope]   = useState('all');
  const [tab, setTab]       = useState('overview');
  const [allInsights, setAllInsights] = useState(false);

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);  // first paint only
  const [busy, setBusy]       = useState(false); // refetch — holds the old render
  const [error, setError]     = useState(null);

  const range = useMemo(
    () => buildRange(rangeKey, customFrom, customTo),
    [rangeKey, customFrom, customTo]
  );
  const rangeDays = useMemo(() => daysBetween(range.from, range.to), [range]);

  const load = useCallback(async (signal) => {
    setError(null);
    setBusy(true);
    try {
      const json = await fetchPortfolio(range, scope, signal);
      setData(json);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message || 'Something went wrong');
    } finally {
      setBusy(false);
      setLoading(false);
    }
  }, [range, scope]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const restaurants = useMemo(() => data?.restaurants || [], [data]);
  const cur  = data?.current;
  const prev = data?.previous;

  /* Colour is keyed to the outlet's position in the owner's stable
     restaurant list, so a given outlet keeps its hue no matter how
     the scope or the sort order changes. */
  const colorOf = useCallback((id) => {
    const idx = restaurants.findIndex((r) => String(r._id) === String(id));
    return SERIES[(idx < 0 ? 0 : idx) % SERIES.length];
  }, [restaurants]);

  // Problems lead: what needs action outranks what is going well.
  const insights = useMemo(
    () => buildInsights(cur, prev, { ...range, days: rangeDays })
      .sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone]),
    [cur, prev, range, rangeDays]
  );

  const heroTrend = useMemo(() => buildTrend(cur, prev), [cur, prev]);

  const scopeLabel = scope === 'all'
    ? `All outlets${restaurants.length ? ` (${restaurants.length})` : ''}`
    : (restaurants.find((r) => String(r._id) === scope)?.name || 'Outlet');

  /* ── First load ── */
  if (loading) {
    return (
      <div className="an-shell">
        <div className="an-loading" role="status" aria-live="polite">
          <span className="an-spinner" aria-hidden="true" />
          <p>Crunching your numbers…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="an-shell">
        <div className="an-error" role="alert">
          <AlertCircle size={30} strokeWidth={1.5} aria-hidden="true" />
          <h2>Could not load analytics</h2>
          <p>{error}</p>
          <button type="button" className="an-btn" onClick={() => load()}>
            <RefreshCw size={15} aria-hidden="true" />Try again
          </button>
        </div>
      </div>
    );
  }

  if (!restaurants.length) {
    return (
      <div className="an-shell">
        <div className="an-error">
          <Store size={30} strokeWidth={1.5} aria-hidden="true" />
          <h2>No restaurants yet</h2>
          <p>Add your first property and analytics will start building from your very first order.</p>
        </div>
      </div>
    );
  }

  const t = cur?.totals || {};
  const p = prev?.totals || {};
  const guests = cur?.customers || {};
  const prevGuests = prev?.customers || {};
  const rating = cur?.ratings?.averages?.experience;
  const prevRating = prev?.ratings?.averages?.experience;

  return (
    <div className="an-shell">
      {/* ── Header: title and the filters that scope everything below
          share one row, so the numbers start higher on the page. The
          subtitle states the comparison window every delta uses. ── */}
      <header className="an-head">
        <div className="an-head__text">
          <h1 className="an-head__title">Analytics</h1>
          <p className="an-head__sub">
            {spanLabel(range.from, range.to)} {range.to.slice(0, 4)}
            <span className="an-head__sep"> · </span>
            <span className="an-head__vs">compared with {spanLabel(range.prevFrom, range.prevTo)}</span>
          </p>
        </div>

      <div className="an-filters">
        <Dropdown label="Period" value={range.label} icon={Clock}>
          {(close) => (
            <>
              {RANGE_PRESETS.map((pr) => (
                <button
                  key={pr.key}
                  type="button"
                  className={`an-dd__opt ${rangeKey === pr.key ? 'is-sel' : ''}`}
                  onClick={() => { setRangeKey(pr.key); close(); }}
                  role="option"
                  aria-selected={rangeKey === pr.key}
                >
                  <Check size={16} className="an-dd__check" aria-hidden="true" />
                  {pr.label}
                </button>
              ))}
              <div className="an-dd__custom">
                <span className="an-dd__customlabel">Custom range</span>
                <div className="an-dd__dates">
                  <input
                    type="date" value={customFrom} max={customTo || iso(new Date())}
                    onChange={(e) => setCustomFrom(e.target.value)} aria-label="From date"
                  />
                  <input
                    type="date" value={customTo} min={customFrom} max={iso(new Date())}
                    onChange={(e) => setCustomTo(e.target.value)} aria-label="To date"
                  />
                </div>
                <button
                  type="button"
                  className="an-btn an-btn--sm"
                  disabled={!customFrom || !customTo}
                  onClick={() => { setRangeKey('custom'); close(); }}
                >
                  Apply
                </button>
              </div>
            </>
          )}
        </Dropdown>

        <Dropdown label="Outlet" value={scopeLabel} icon={Store}>
          {(close) => (
            <>
              <button
                type="button"
                className={`an-dd__opt ${scope === 'all' ? 'is-sel' : ''}`}
                onClick={() => { setScope('all'); close(); }}
                role="option" aria-selected={scope === 'all'}
              >
                <Check size={16} className="an-dd__check" aria-hidden="true" />
                All outlets ({restaurants.length})
              </button>
              <div className="an-dd__sep" />
              {restaurants.map((r) => (
                <button
                  key={String(r._id)}
                  type="button"
                  className={`an-dd__opt ${scope === String(r._id) ? 'is-sel' : ''}`}
                  onClick={() => { setScope(String(r._id)); close(); }}
                  role="option" aria-selected={scope === String(r._id)}
                >
                  <Check size={16} className="an-dd__check" aria-hidden="true" />
                  <i className="an-dd__swatch" style={{ background: colorOf(r._id) }} aria-hidden="true" />
                  {r.name}
                </button>
              ))}
            </>
          )}
        </Dropdown>

        {scope !== 'all' && (
          <button type="button" className="an-chip" onClick={() => setScope('all')}>
            Clear outlet filter
          </button>
        )}

        <button
          type="button"
          className="an-icnbtn"
          onClick={() => load()}
          disabled={busy}
          title="Refresh"
          aria-label="Refresh analytics"
        >
          <RefreshCw size={15} className={busy ? 'is-spinning' : ''} aria-hidden="true" />
        </button>
      </div>
      </header>

      {/* Refetch holds the previous render at reduced opacity rather
          than flashing a skeleton and jumping the layout. */}
      <div className={`an-body ${busy ? 'is-busy' : ''}`}>

        {/* ── Headline numbers: revenue leads as the hero; six tiles
            form an even 2×3 / 3×2 block so no tile is ever orphaned
            on its own row. ── */}
        <section className="an-kpis" aria-label="Headline numbers">
          <div className="an-hero__main">
            <div className="an-hero__figs">
              <span className="an-hero__label">Net revenue</span>
              <span className="an-hero__value"><Figure value={t.revenue} money max={13} /></span>
              <Delta curr={t.revenue} prev={prev ? p.revenue : undefined} />
              {prev && (
                <span className="an-hero__prev">
                  Previous period <strong><Figure value={p.revenue} money max={13} /></strong>
                </span>
              )}
            </div>
            {heroTrend.length > 1 && <Sparkline data={heroTrend} />}
          </div>

          <div className="an-tilerow an-tilerow--kpi">
            <StatTile
              label="Orders" icon={ShoppingBag}
              value={<Figure value={t.orders} />} curr={t.orders} prev={prev ? p.orders : undefined}
            />
            <StatTile
              label="Average order" icon={Wallet}
              value={<Figure value={t.avgOrderValue} money />} curr={t.avgOrderValue} prev={prev ? p.avgOrderValue : undefined}
            />
            <StatTile
              label="Guests served" icon={Users}
              value={<Figure value={guests.customers} />} curr={guests.customers} prev={prev ? prevGuests.customers : undefined}
            />
            <StatTile
              label="Guest rating" icon={Star}
              value={rating == null ? '—' : `${rating.toFixed(1)}/5`}
              curr={rating} prev={prev && rating != null ? prevRating : undefined}
              hint={`${n0(cur?.ratings?.count || 0)} ratings`}
            />
            <StatTile
              label="Cancelled" icon={XCircle}
              value={pct1(t.cancellationRate)}
              curr={t.cancellationRate} prev={prev ? p.cancellationRate : undefined}
              goodWhen="down"
              tone={t.cancellationRate >= 5 ? 'warn' : undefined}
            />
            <StatTile
              label="Uncollected" icon={AlertCircle}
              value={<Figure value={t.outstanding} money />}
              curr={t.outstanding} prev={prev ? p.outstanding : undefined}
              goodWhen="down"
              tone={t.outstanding > 0 ? 'warn' : undefined}
            />
          </div>
        </section>

        {/* ── Plain-language read. Capped so it informs rather than
            pushes the charts below the fold; problems surface first. ── */}
        {insights.length > 0 && (
          <section className="an-insights" aria-label="Key takeaways">
            <div className="an-insights__list">
              {(allInsights ? insights : insights.slice(0, INSIGHTS_VISIBLE)).map((ins, i) => (
                <p key={i} className={`an-insight an-insight--${ins.tone}`}>
                  <span className="an-insight__mark" aria-hidden="true" />
                  {ins.text}
                </p>
              ))}
            </div>
            {insights.length > INSIGHTS_VISIBLE && (
              <button
                type="button"
                className="an-linkbtn"
                aria-expanded={allInsights}
                onClick={() => setAllInsights((v) => !v)}
              >
                {allInsights ? 'Show fewer' : `Show ${insights.length - INSIGHTS_VISIBLE} more takeaways`}
                <ChevronDown size={14} aria-hidden="true" className={allInsights ? 'is-flipped' : ''} />
              </button>
            )}
          </section>
        )}

        {/* ── Tabs ── */}
        <nav className="an-tabs" role="tablist" aria-label="Analytics sections">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`an-tab ${tab === key ? 'is-active' : ''}`}
              onClick={() => setTab(key)}
            >
              <Icon size={15} aria-hidden="true" />
              {label}
            </button>
          ))}
        </nav>

        <div role="tabpanel" className="an-panel">
          {!cur ? (
            <EmptyState title="No data for this period" />
          ) : tab === 'overview' ? (
            <OverviewTab cur={cur} prev={prev} range={{ ...range, days: rangeDays }} />
          ) : tab === 'outlets' ? (
            <OutletsTab cur={cur} prev={prev} colorOf={colorOf} restaurants={restaurants} onFocus={setScope} />
          ) : tab === 'guests' ? (
            <GuestsTab cur={cur} prev={prev} />
          ) : tab === 'reputation' ? (
            <ReputationTab cur={cur} prev={prev} />
          ) : (
            <ReachTab cur={cur} prev={prev} restaurants={restaurants} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
